// src/services/sociosService.js
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  runTransaction,
  query,
  where,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../Firebase/firebaseconfig";

export const sociosCol = collection(db, "socios");
export const propiedadesCol = collection(db, "propiedades");

const nombreCompletoSocio = (socio = {}) =>
  `${socio.nombre || ""} ${socio.apellidos || ""}`.trim() || socio.razonSocial || "Socio";

export const crearSocio = async (payload) => {
  const now = serverTimestamp();
  return addDoc(sociosCol, {
    nombre: payload.nombre || "",
    apellidos: payload.apellidos || "",
    telefono: payload.telefono || "",
    email: payload.email || "",
    direccion: payload.direccion || "",
    notas: payload.notas || "",
    activo: payload.activo ?? true,
    creado: now,
    actualizado: now,
  });
};

export const actualizarSocio = async (socioId, payload) => {
  return updateDoc(doc(db, "socios", socioId), {
    nombre: payload.nombre || "",
    apellidos: payload.apellidos || "",
    telefono: payload.telefono || "",
    email: payload.email || "",
    direccion: payload.direccion || "",
    notas: payload.notas || "",
    activo: payload.activo ?? true,
    actualizado: serverTimestamp(),
  });
};

export const obtenerSocio = async (socioId) => {
  const snap = await getDoc(doc(db, "socios", socioId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};

export const listarSocios = async () => {
  const snap = await getDocs(sociosCol);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const listarPropiedadesDeSocio = async (socioId) => {
  const q = query(propiedadesCol, where("socioId", "==", socioId));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const listarPropiedadesDisponibles = async () => {
  const q = query(
    propiedadesCol,
    where("activo", "==", true),
    where("disponible", "==", true)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const asignarPropiedadesASocio = async ({ socioId, propiedadIds, usuario }) => {
  if (!socioId) throw new Error("Falta socioId");
  if (!Array.isArray(propiedadIds) || propiedadIds.length === 0) {
    throw new Error("Selecciona al menos una propiedad");
  }

  const socioRef = doc(db, "socios", socioId);
  const propiedadRefs = propiedadIds.map((id) => doc(db, "propiedades", id));

  await runTransaction(db, async (tx) => {
    const socioSnap = await tx.get(socioRef);
    if (!socioSnap.exists()) throw new Error("El socio no existe");

    const socio = { id: socioSnap.id, ...socioSnap.data() };
    const socioNombre = nombreCompletoSocio(socio);
    const fecha = Timestamp.now();

    const snaps = [];
    for (const ref of propiedadRefs) {
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error("Una propiedad ya no existe");
      const p = snap.data();
      if (p.activo === false || p.disponible !== true || p.estado === "vendida") {
        throw new Error(`La propiedad ${p.folio || snap.id} ya no está disponible`);
      }
      snaps.push({ ref, snap, data: p });
    }

    for (const item of snaps) {
      const p = item.data;
      const bitacora = Array.isArray(p.bitacora) ? p.bitacora : [];
      tx.update(item.ref, {
        estado: "apartada_socio",
        disponible: false,
        apartadaSocio: true,
        socioId,
        socioNombre,
        fechaApartadoSocio: fecha,
        actualizado: fecha,
        bitacora: [
          ...bitacora,
          {
            tipo: "apartada_socio",
            detalle: `Propiedad apartada para socio: ${socioNombre}`,
            socioId,
            socioNombre,
            usuarioId: usuario?.uid || null,
            usuarioNombre: usuario?.displayName || usuario?.email || null,
            ts: fecha,
          },
        ],
      });
    }

    tx.update(socioRef, {
      actualizado: fecha,
      propiedadesAsignadasCount: (socio.propiedadesAsignadasCount || 0) + propiedadRefs.length,
    });
  });
};

export const liberarPropiedadDeSocio = async ({ propiedadId, motivo, usuario }) => {
  if (!propiedadId) throw new Error("Falta propiedadId");

  const propiedadRef = doc(db, "propiedades", propiedadId);

  await runTransaction(db, async (tx) => {
    const propSnap = await tx.get(propiedadRef);
    if (!propSnap.exists()) throw new Error("La propiedad no existe");

    const p = propSnap.data();
    if (!p.apartadaSocio && p.estado !== "apartada_socio") {
      throw new Error("La propiedad no está apartada para socio");
    }

    const fecha = Timestamp.now();
    const socioId = p.socioId || null;
    const socioNombre = p.socioNombre || "Socio";
    const bitacora = Array.isArray(p.bitacora) ? p.bitacora : [];

    let socioSnap = null;
    let socioRef = null;
    if (socioId) {
      socioRef = doc(db, "socios", socioId);
      socioSnap = await tx.get(socioRef);
    }

    tx.update(propiedadRef, {
      estado: "disponible",
      disponible: true,
      apartadaSocio: false,
      socioId: null,
      socioNombre: null,
      fechaLiberacionSocio: fecha,
      actualizado: fecha,
      bitacora: [
        ...bitacora,
        {
          tipo: "liberada_socio",
          detalle: motivo || "Propiedad regresada por socio",
          socioId,
          socioNombre,
          usuarioId: usuario?.uid || null,
          usuarioNombre: usuario?.displayName || usuario?.email || null,
          ts: fecha,
        },
      ],
    });

    if (socioRef && socioSnap?.exists()) {
      const s = socioSnap.data();
      tx.update(socioRef, {
        actualizado: fecha,
        propiedadesAsignadasCount: Math.max(0, Number(s.propiedadesAsignadasCount || 0) - 1),
      });
    }
  });
};
